import React, { useState, useRef, useEffect} from 'react';
import { Calendar, MapPin, Utensils, Music, Heart, CheckCircle2, ChevronLeft, ChevronRight, Play} from 'lucide-react';

const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      return initialValue;
    }
  });

  const setValue = value => {
    try {
      setStoredValue(value);
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(error);
    }
  };

  return [storedValue, setValue];
};

const Card = ({ item, image, category, onToggleFavorite, onToggleComplete, isFavorite, isCompleted }) => {
    const [showActions, setShowActions] = useState(false);
    
    const [longPressTimer, setLongPressTimer] = useState(null);
    
    const handleTouchStart = () => {
      const timer = setTimeout(() => {
        setShowActions(true);
      }, 500);
      setLongPressTimer(timer);
    };
  
    const handleTouchEnd = () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
      }
    };
  
    return (
      <div className="flex-none relative group/item first:ml-[8px] md:first:ml-[16px]"
           onTouchStart={handleTouchStart}
           onTouchEnd={handleTouchEnd}>
        <div className="w-[240px] h-[135px] md:w-[280px] md:h-[157px] rounded-md overflow-hidden transition-all duration-300 ease-out origin-center transform-gpu group-hover/item:scale-[1.2] group-hover/item:z-50 group-hover/item:delay-200 group-hover/item:shadow-2xl relative">
          <img 
            src={image}
            alt={item}
            className="absolute inset-0 w-full h-full object-cover"
          />
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent group-hover/item:opacity-90 transition-opacity">
            {/* Action buttons - Note the modified opacity conditions */}
            <div className={`absolute top-2 right-2 flex gap-2 transition-opacity duration-300 
              ${(showActions || isCompleted || isFavorite) ? 'opacity-100' : 'opacity-0 group-hover/item:opacity-100'}`}>
              {/* Heart Button - Always visible when favorited */}
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite(category, item);
                }}
                className={`p-2 rounded-full bg-black/50 hover:bg-black/80 transition-colors z-50 ${isFavorite ? 'opacity-100' : ''}`}
              >
                <Heart className={`w-5 h-5 transition-colors ${isFavorite ? 'text-red-500 fill-red-500' : 'text-white'}`} />
              </button>
              
              {/* Complete Button */}
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleComplete(category, item);
                }}
                className="p-2 rounded-full bg-black/50 hover:bg-black/80 transition-colors z-50"
              >
                <CheckCircle2 className={`w-5 h-5 transition-colors ${isCompleted ? 'text-green-500 fill-green-500' : 'text-white'}`} />
              </button>
            </div>
  
            {/* Rest of the card content remains the same */}
            {isCompleted && (
              <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                Completed ✨
              </div>
            )}
  
            <div className="absolute inset-0 flex items-center justify-center p-4 opacity-0 group-hover/item:opacity-100 transition-opacity duration-300">
              <p className="text-white text-center text-sm md:text-base font-medium">
                {item}
              </p>
            </div>
            
            <div className="absolute inset-x-0 bottom-0 p-3">
              <p className="text-white text-sm md:text-base group-hover/item:opacity-0 transition-opacity duration-300 font-medium line-clamp-2">
                {item}
              </p>
            </div>
          </div>
        </div>
  
        {/* Info panel */}
        <div className="absolute top-full left-0 w-full opacity-0 group-hover/item:opacity-100 transition-all duration-300 delay-200 z-50 scale-[1.2] origin-top pointer-events-none">
          <div className="bg-[#141414] rounded-b-md shadow-xl p-3 transform -translate-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-white/70">
                {isCompleted ? 'Completed ✨' : 'Long press to mark as done'}
              </span>
              <span className="text-white/70">
                {isFavorite ? '❤️' : '🤍'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const Header = ({ isScrolled }) => {
    return (
        <header className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
            isScrolled
                ? 'bg-[#141414]/95 backdrop-blur-sm shadow-lg'
                : 'bg-gradient-to-b from-black/80 to-transparent'
        }`}>
            <nav className="container mx-auto px-4 py-4 flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0">
                <div className="flex items-center space-x-2">
                    <Heart className="text-red-500" size={24} />
                    <span className="text-white font-semibold text-lg sm:text-xl">Our Journey</span>
                </div>
                <div className="flex items-center gap-3 sm:gap-6 text-sm sm:text-base">
                    <button className="text-gray-300 hover:text-white transition-colors px-2 py-1">
                        Adventures
                    </button>
                    <button className="text-gray-300 hover:text-white transition-colors px-2 py-1">
                        Moments
                    </button>
                    <button className="text-gray-300 hover:text-white transition-colors px-2 py-1">
                        Memories
                    </button>
                </div>
            </nav>
        </header>
    );
};

  const Modal = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop with heart pattern */}
            <div 
                className="absolute inset-0 bg-black/75 backdrop-blur-sm"
                onClick={onClose}
                style={{
                    background: `
                        radial-gradient(circle at 20% 20%, rgba(239, 68, 68, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 80% 80%, rgba(239, 68, 68, 0.1) 0%, transparent 50%),
                        rgba(0, 0, 0, 0.75)
                    `
                }}
            />
            {/* Modal content */}
            <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 text-white border border-red-500/20 rounded-lg max-w-2xl w-full mx-4 shadow-xl overflow-hidden">
                {/* Decorative hearts */}
                <div className="absolute top-0 left-0 w-16 h-16 text-red-500/10">
                    <Heart className="w-full h-full" />
                </div>
                <div className="absolute bottom-0 right-0 w-16 h-16 text-red-500/10">
                    <Heart className="w-full h-full" />
                </div>
                
                <div className="relative p-6">
                    {children}
                </div>
            </div>
        </div>
    );
};

const ContentRow = ({ 
    title, 
    icon: Icon, 
    items, 
    category, 
    favorites, 
    completed,
    onToggleFavorite,
    onToggleComplete, // Added this prop
    images 
  }) => {
    const [isHovered, setIsHovered] = useState(false);
    const sliderRef = useRef(null);
  
    const scroll = (direction) => {
      const scrollAmount = 1000;
      if (sliderRef.current) {
        const newScrollPosition = sliderRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
        sliderRef.current.scrollTo({
          left: newScrollPosition,
          behavior: 'smooth'
        });
      }
    };
  
    return (
      <div 
        className="group relative px-4 md:px-12 mb-8 md:mb-12"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <h2 className="flex items-center gap-2 text-lg md:text-xl text-gray-200 font-medium mb-2 ml-2 transition-opacity duration-200 group-hover:text-white">
          <Icon className="w-5 h-5" />
          {title}
        </h2>
  
        {isHovered && (
          <>
            <button 
              onClick={() => scroll('left')}
              className="absolute left-0 top-[55%] z-40 h-[140px] w-12 bg-black/50 -translate-y-1/2 hover:bg-black/80 transition-colors hidden md:flex items-center justify-center group-hover:opacity-100 opacity-0 transition-opacity duration-300"
            >
              <ChevronLeft className="w-8 h-8 text-white" />
            </button>
            <button 
              onClick={() => scroll('right')}
              className="absolute right-0 top-[55%] z-40 h-[140px] w-12 bg-black/50 -translate-y-1/2 hover:bg-black/80 transition-colors hidden md:flex items-center justify-center group-hover:opacity-100 opacity-0 transition-opacity duration-300"
            >
              <ChevronRight className="w-8 h-8 text-white" />
            </button>
          </>
        )}
  
        <div className="relative ml-[-8px] md:ml-[-16px]">
          <div 
            ref={sliderRef}
            className="flex gap-2 overflow-x-auto no-scrollbar pb-12 pt-2 pl-2"
            style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}
          >
            {items.map((item, index) => (
              <Card 
                key={index} 
                item={item} 
                image={images?.[index % (images?.length || 1)] || `/api/placeholder/400/300?text=${encodeURIComponent(item)}`}
                category={category}
                onToggleFavorite={onToggleFavorite}
                onToggleComplete={onToggleComplete} // Pass the prop
                isFavorite={favorites.some(fav => fav.item === item)}
                isCompleted={completed?.some(comp => comp.item === item)} // Added completed check
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const RoadmapPage = () => {
    const [favorites, setFavorites] = useLocalStorage('favorites', []);
    const [completed, setCompleted] = useLocalStorage('completed', []);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [showModal, setShowModal] = useState(false);
    

    const heroImages = [
        "/assets/images/goals.jpg",
        "/assets/images/champorado.jpg",
        "/assets/images/read.jpg",
    ];

    const dateImages = [
        "/assets/images/stargazing.jpg",
        "/assets/images/drive.jpg",
        "/assets/images/cooking.jpg",
        "/assets/images/art.jpg",
        "/assets/images/rock.jpg",
        "/assets/images/movie.jpg",
        "/assets/images/board.jpg",
    ];

    const placeImages = [
        "/assets/images/flowers.jpg",
        "/assets/images/coffee.jpg",
        "/assets/images/beach.jpg",
        "/assets/images/mountain.jpg",
        "/assets/images/historical.jpg",
        "/assets/images/botanical.jpg",
        "/assets/images/rooftop.jpg",
    ];

    const foodImages = [
        "/assets/images/pasta.jpg",
        "/assets/images/icecream.jpg",
        "/assets/images/sushi.jpg",
        "/assets/images/champorado.jpg",
        "/assets/images/chocolate.jpg",
        "/assets/images/brunch.jpg",
        "/assets/images/pizza.jpg",
    ];

    const momentImages = [
        "/assets/images/playlist.jpg",
        "/assets/images/sunrise.jpg",
        "/assets/images/plant.jpg",
        "/assets/images/working.jpg",
        "/assets/images/album.jpg",
        "/assets/images/karaoke.jpg",
        "/assets/images/hobby.jpg",
    ];

    const content = [
        {
            title: "Featured Date Ideas",
            icon: Calendar,
            category: "dates",
            images: dateImages,
            items: [
                "Stargazing picnic under the night sky ✨",
                "Long drive together with no destination 🚗",
                "Cooking class for two 👩‍🍳",
                "Art gallery hopping 🎨",
                "Indoor rock climbing adventure 🧗‍♀️",
                "Movie marathon night 🎬",
                "Board game café visit 🎲"
            ]
        },
        {
            title: "Places To Go",
            icon: MapPin,
            category: "places",
            images: placeImages,
            items: [
                "Flowers field park 🌸",
                "That cozy café ☕",
                "Beach sunset walks 🌅",
                "Mountain hiking trails 🏔️",
                "Historical downtown tour 🏛️",
                "Botanical gardens 🌿",
                "Rooftop restaurant with city views 🌆"
            ]
        },
        {
            title: "Food Adventures",
            icon: Utensils,
            category: "food",
            images: foodImages,
            items: [
                "Your favorite pasta place 🍝",
                "Ice cream sundae date 🍨",
                "Sushi night 🍱",
                "The champorado you're craving 🍫",
                "Chocolate tasting experience 🍫",
                "Brunch at that new café ☕",
                "Pizza night date 🍕"
            ]
        },
        {
            title: "Moments To Create",
            icon: Music,
            category: "moments",
            images: momentImages,
            items: [
                "Create our playlist together 🎵",
                "Watch sunrise at our spot 🌄",
                "Plant together 🌱",
                "Working out together 💪",
                "Start a photo diary 📸",
                "Karaoke night 🎤",
                "Learn a new hobby together 🎨"
            ]
        }
    ];

     // Handle scroll effect
     useEffect(() => {
      const handleScroll = () => {
          setIsScrolled(window.scrollY > 0);
      };
  
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Handle image rotation with pause functionality
  useEffect(() => {
      if (isPaused) return;
      
      const interval = setInterval(() => {
          setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
      }, 5000);
  
      return () => clearInterval(interval);
  }, [heroImages.length, isPaused]);

  const handlePrevImage = () => {
      setCurrentImageIndex((prev) => 
          prev === 0 ? heroImages.length - 1 : prev - 1
      );
  };

  const handleNextImage = () => {
      setCurrentImageIndex((prev) => 
          (prev + 1) % heroImages.length
      );
  };


    // Calculate total items from content sections
    const totalItems = content.reduce((acc, section) => acc + section.items.length, 0);

    // Create a mapping of all items to their images and categories
    const itemMapping = {};
    content.forEach(section => {
        section.items.forEach((item, index) => {
            itemMapping[item] = {
                image: section.images[index],
                category: section.category
            };
        });
    });

    const handleToggleFavorite = (category, item) => {
        setFavorites(prevFavorites => {
            const existingFavorite = prevFavorites.find(fav => fav.item === item);
            if (existingFavorite) {
                return prevFavorites.filter(fav => fav.item !== item);
            } else {
                return [...prevFavorites, { 
                    category, 
                    item,
                    image: itemMapping[item].image // Add the corresponding image
                }];
            }
        });
    };

    const handleToggleComplete = (category, item) => {
        setCompleted(prevCompleted => {
            const existingComplete = prevCompleted.find(comp => comp.item === item);
            if (existingComplete) {
                return prevCompleted.filter(comp => comp.item !== item);
            } else {
                return [...prevCompleted, { 
                    category, 
                    item,
                    image: itemMapping[item].image, // Add the corresponding image
                    completedAt: new Date().toISOString(),
                }];
            }
        });
    };

    // Create favorites section if there are favorites
    const favoritesSection = favorites.length > 0 ? {
        title: "My Favorites ❤️",
        icon: Heart,
        category: "favorites",
        items: favorites.map(fav => fav.item),
        images: favorites.map(fav => itemMapping[fav.item].image) // Use mapped images
    } : null;

    return (
        <div className="min-h-screen bg-[#141414] overflow-x-hidden">
        {/* Header */}
        <Header isScrolled={isScrolled} />

        {/* Hero section with enhanced functionality */}
        <div 
            className="relative h-[85vh] mb-1"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            {/* Images */}
            {heroImages.map((image, index) => (
                <div
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-1000 ${
                        index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                    }`}
                >
                    <img
                        src={image}
                        alt={`Hero background ${index + 1}`}
                        className="w-full h-full object-cover"
                    />
                </div>
            ))}

            {/* Gradient overlay */}
            <div className={`absolute inset-0 ${
                currentImageIndex === 1 
                ? 'bg-gradient-to-b from-black/20 via-transparent to-[#141414]/90' 
                : 'bg-gradient-to-b from-black/50 via-[#141414]/80 to-[#141414]'
            }`}>
                {/* Content that changes based on current image */}
                {currentImageIndex === 0 && (
                    <div className="absolute bottom-32 left-12 max-w-xl">
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">Our Story</h1>
                        <p className="text-lg text-gray-200">
                            A collection of adventures waiting to unfold. Each card represents a moment we'll share together.
                        </p>
                    </div>
                )}
                
                {currentImageIndex === 1 && (
                  <div className="absolute bottom-32 left-12 max-w-xl">
                      <div className="flex items-center gap-4">
                          <button 
                              onClick={() => handleToggleFavorite("food", "The champorado you're craving 🍫")}
                              className="group flex items-center gap-3 px-6 py-4 bg-black/40 hover:bg-black/50 backdrop-blur-md transition-all duration-300 rounded-full text-white border border-white/20 hover:border-white/30 shadow-lg hover:shadow-xl"
                          >
                              <Heart className="w-5 h-5 text-red-400 transition-transform duration-300 group-hover:scale-110" />
                              <span className="font-medium tracking-wide">Add to Favorites</span>
                          </button>
                          
                          <button 
                              onClick={() => handleToggleComplete("food", "The champorado you're craving 🍫")}
                              className="group flex items-center gap-3 px-6 py-4 bg-black/40 hover:bg-black/50 backdrop-blur-md transition-all duration-300 rounded-full text-white border border-white/20 hover:border-white/30 shadow-lg hover:shadow-xl"
                          >
                              <CheckCircle2 className="w-5 h-5 text-green-400 transition-transform duration-300 group-hover:scale-110" />
                              <span className="font-medium tracking-wide">Add to Completed</span>
                          </button>
                      </div>
                  </div>
                )}
                
                {currentImageIndex === 2 && (
                    <div className="absolute bottom-32 left-12 flex items-center">
                        <button
                            onClick={() => setShowModal(true)}
                            className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 transition-colors rounded-full text-white"
                        >
                            <Play size={24} />
                            <span>Play Message</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Navigation arrows */}
            <button 
                onClick={handlePrevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/75 rounded-full text-white transition-colors"
            >
                <ChevronLeft size={24} />
            </button>
            <button 
                onClick={handleNextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/75 rounded-full text-white transition-colors"
            >
                <ChevronRight size={24} />
            </button>

            {/* Navigation dots */}
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2">
                {heroImages.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                            index === currentImageIndex 
                                ? 'bg-white w-4' 
                                : 'bg-white/50 hover:bg-white/75'
                        }`}
                    />
                ))}
            </div>
        </div>

        <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
    {/* Using max-height and overflow-y-auto for scrollability */}
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
        {/* Backdrop */}
        <div 
            className="absolute inset-0 bg-black/75 backdrop-blur-sm"
            // onClick={onClose}
            style={{
                background: `
                    radial-gradient(circle at 20% 20%, rgba(239, 68, 68, 0.1) 0%, transparent 50%),
                    radial-gradient(circle at 80% 80%, rgba(239, 68, 68, 0.1) 0%, transparent 50%),
                    rgba(0, 0, 0, 0.75)
                `
            }}
        />
        
        {/* Modal Content */}
        <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 text-white border border-red-500/20 rounded-lg w-full max-w-2xl mx-auto my-8 shadow-xl overflow-hidden">
            <div className="max-h-[80vh] overflow-y-auto p-6">
                <div className="text-center mb-6">
                    <div className="flex justify-center mb-4">
                        <Heart className="text-red-500 w-12 h-12" />
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-2">My Valentine's Message</h2>
                    <p className="text-red-300 text-sm">With love and hope ❤️</p>
                </div>
                
                <div className="space-y-6">
                    <p className="text-gray-300 leading-relaxed italic text-lg text-center">
                        "It's funny how life works sometimes. The best surprises come when you least expect them."
                    </p>
                    
                    <div className="bg-gray-800/50 p-4 md:p-6 rounded-lg border border-red-500/20">
                        <p className="text-gray-300 leading-relaxed mb-4">
                            My dearest,
                        </p>
                        <p className="text-gray-300 leading-relaxed mb-4 text-left">
                            It's funny how life works sometimes. Three years ago, we shared moments and played together that became precious memories. I'm sorry for how things ended, and now here we are again, picking up conversations as if time barely passed. They say timing is everything, and maybe that's why our paths have crossed again.
                        </p>
                        <p className="text-gray-300 leading-relaxed mb-4 text-left">
                            This Valentine's Day, I want you to know that reconnecting with you has brought warmth to my days, seriously. There's something special about talking to you; just the thought of you is enough to make me smile, to make my day, to help me forget how tiring and unfair this world is.
                        </p>
                        <p className="text-gray-300 leading-relaxed mb-4 text-left">
                            I appreciate that you have given me another chance and allowed me to enter your life again, this time with more life experience and new stories to share. I'm really grateful for it. This time, I'll never let you go and I'll never repeat the same mistakes that I did.
                        </p>
                        <p className="text-gray-300 leading-relaxed italic text-left">
                            P.S. I still remember our memories from three years ago, don't you?
                        </p>
                        <p className="text-gray-300 leading-relaxed mt-6">
                            With hope for our future,<br />
                            Juan Miguel Sanchez
                        </p>
                    </div>

                    <div className="pt-4 text-center">
                        <button
                            onClick={() => setShowModal(false)}
                            className="px-8 py-3 bg-red-600 hover:bg-red-700 transition-colors rounded-full text-white font-medium"
                        >
                            Close with Love ❤️
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</Modal>
  
      {/* Stats section */}
      <div className="px-4 md:px-12 mb-8">
        <div className="bg-gray-900/50 rounded-lg p-4 flex justify-around">
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{completed.length}</p>
            <p className="text-sm text-gray-400">Completed</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{favorites.length}</p>
            <p className="text-sm text-gray-400">Favorites</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">
              {Math.round((completed.length / totalItems) * 100)}%
            </p>
            <p className="text-sm text-gray-400">Progress</p>
          </div>
        </div>
      </div>
  
      {/* Content rows */}
      <div className="relative z-10">
        {/* Show completed section first */}
        {completed.length > 0 && (
                    <ContentRow
                        key="completed"
                        title="Completed Adventures ✨"
                        items={completed.map(comp => comp.item)}
                        images={completed.map(comp => comp.image)} // Use stored images
                        icon={CheckCircle2}
                        category="completed"
                        favorites={favorites}
                        completed={completed}
                        onToggleFavorite={handleToggleFavorite}
                        onToggleComplete={handleToggleComplete}
                    />
                )}

                {/* Show favorites section if it exists */}
                {favoritesSection && (
                    <ContentRow
                        key="favorites"
                        {...favoritesSection}
                        favorites={favorites}
                        completed={completed}
                        onToggleFavorite={handleToggleFavorite}
                        onToggleComplete={handleToggleComplete}
                    />
                )}

                {/* Map through content sections, filtering out completed items */}
                {content.map((section, index) => {
                    // Filter out completed items from each section
                    const filteredItems = section.items.filter(item =>
                        !completed.some(comp => comp.item === item)
                    );

                    // Get corresponding images for filtered items
                    const filteredImages = filteredItems.map(item => itemMapping[item].image);

                    // Only render section if it has non-completed items
                    if (filteredItems.length === 0) return null;

                    return (
                        <ContentRow
                            key={index}
                            title={section.title}
                            items={filteredItems}
                            images={filteredImages}
                            icon={section.icon}
                            category={section.category}
                            favorites={favorites}
                            completed={completed}
                            onToggleFavorite={handleToggleFavorite}
                            onToggleComplete={handleToggleComplete}
                        />
                    );
                })}
            </div>
        </div>
    );
};

// Global styles unchanged
const globalStyles = `
  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
  
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const styleTag = document.createElement('style');
styleTag.textContent = globalStyles;
document.head.appendChild(styleTag);

export default RoadmapPage;
